import { MemoizedSelector, select, Store } from '@ngrx/store';
import { filter, takeWhile } from 'rxjs/operators';
import {
  OnboardingActionOnboardingCompleted,
  OnboardingActionSendStepV2
} from '../onboarding.actions';
import {
  Onboarding,
  OnboardingSteps,
  OnboardingStepTypes
} from '../onboarding.models';

interface Bullet {
  color: string;
  width: string;
  display: string;
  stepColor: string;
  marginRight: string;
}

export class StepsManagerV2 {
  bullets: Array<Bullet> = [];

  private alive = true;
  private currentIndex = 0;
  private onboarding: Onboarding;

  get currentStep(): OnboardingSteps {
    return (
      (this.onboarding &&
        this.onboarding.steps &&
        this.onboarding.steps.length &&
        this.onboarding.steps[this.currentIndex]) ||
      void 0
    );
  }

  get currentStepValues(): object {
    if (this.currentStep.type === OnboardingStepTypes.TOS) {
      return this.prepareTosPayload(this.currentStep);
    }

    return {
      ...this.currentStep.formValue,
      step: this.currentStep.uuid
    };
  }

  get displayPrevBtn(): boolean {
    return this.onboardingStepIndex(this.currentStep) > 0;
  }

  get displayNextBtn(): boolean {
    return (
      this.currentStep.type === OnboardingStepTypes.FORM && !this.isLastStep()
    );
  }

  get prevBtnColor(): string {
    return this.currentIndex > 0
      ? this.onboarding.steps[this.currentIndex - 1].color
      : this.onboarding.steps[this.currentIndex].color;
  }

  get nextBtnColor(): string {
    return this.currentIndex < this.onboarding.steps.length - 1
      ? this.onboarding.steps[this.currentIndex + 1].color
      : this.onboarding.steps[this.currentIndex].color;
  }

  constructor(
    private store: Store<any>,
    onboardingSelector: MemoizedSelector<object, Onboarding>
  ) {
    this.store
      .pipe(
        select(onboardingSelector),
        filter(onboarding => !!onboarding),
        takeWhile(() => this.alive)
      )
      .subscribe(onboarding => {
        this.currentIndex = 0;
        this.onboarding = onboarding;
        this.createBullets();
      });
  }

  destroy(): void {
    this.alive = false;
    this.currentIndex = 0;
    this.onboarding = void 0;
  }

  previousStep(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateBullets();
    }
  }

  prepareTosPayload(formValues) {
    const obj = {
      'onboarding-tos': []
    };

    formValues.items.forEach(value => {
      const key = value.uuid;
      const val = value.required ? 1 : 0;
      const toggle = { [key]: val };
      obj['onboarding-tos'].push(toggle);
    });

    return {
      ...obj,
      step: this.currentStep.uuid,
      id: this.currentStep.stepUuid,
      is_form_fully_completed: 1
    };
  }

  submitStep(): void {
    if (
      this.currentStep.type === OnboardingStepTypes.INTRO ||
      this.currentStep.type === OnboardingStepTypes.CONCLUSION
    ) {
      this.nextStep();
    } else {
      this.store.dispatch(new OnboardingActionSendStepV2(this));
    }
  }

  stepSubmissionSuccess(): void {
    if (this.isLastOnboardingStep()) {
      this.finalizeSubmissionSuccess();
    } else {
      this.nextStep();
    }
  }

  finalizeSubmissionSuccess(): void {
    this.nextStep();
  }

  private nextStep(): void {
    if (this.isLastStep()) {
      // Dispatch an for onboarding completed
      this.store.dispatch(new OnboardingActionOnboardingCompleted());
    } else {
      this.currentIndex++;
      this.updateBullets();
    }
  }

  private onboardingSteps(): Array<OnboardingSteps> {
    return this.onboarding.steps.filter(
      step =>
        step.type !== OnboardingStepTypes.INTRO &&
        step.type !== OnboardingStepTypes.CONCLUSION
    );
  }

  private onboardingStepIndex(desiredStep: OnboardingSteps): number {
    return this.onboardingSteps().indexOf(desiredStep);
  }

  private isLastStep(desiredStep = this.currentStep): boolean {
    return [...this.onboarding.steps].reverse().indexOf(desiredStep) === 0;
  }

  private isLastOnboardingStep(desiredStep = this.currentStep): boolean {
    return this.onboardingSteps().reverse().indexOf(desiredStep) === 0;
  }

  private createBullets(): void {
    this.bullets =
      this.onboarding && this.onboarding.steps
        ? this.onboarding.steps.map(step => ({
            color: '',
            width: '',
            stepColor: this.getColor(step.type) || step.color,
            marginRight: '20px',
            display:
              step.type === OnboardingStepTypes.TOS ||
              step.type === OnboardingStepTypes.INTRO ||
              step.type === OnboardingStepTypes.PASSWORD ||
              step.type === OnboardingStepTypes.CONCLUSION
                ? 'block'
                : 'none'
          }))
        : [];

    this.bullets.forEach((step, idx, arr) => {
      if (
        step.display === 'none' ||
        (idx < arr.length - 1 && arr[idx + 1].display === 'none')
      ) {
        step.marginRight = '0';
      }
    });

    this.updateBullets();
  }

  private updateBullets(): void {
    this.bullets.forEach((bullet, idx) => {
      bullet.width = idx === this.currentIndex ? '30px' : '12px';
      bullet.color = bullet.stepColor;
      // bullet.color = idx > this.currentIndex ? '#2e2e2e' : bullet.stepColor;
    });
  }

  private getColor(stepType): string {
    let stepColor: string = null;

    switch (stepType) {
      case OnboardingStepTypes.TOS: {
        stepColor = '#262B64';
        break;
      }
      case OnboardingStepTypes.INTRO: {
        stepColor = '#FFD44D';
        break;
      }
      case OnboardingStepTypes.PASSWORD: {
        stepColor = '#9FC490';
        break;
      }
      case OnboardingStepTypes.CONCLUSION: {
        stepColor = '#1488A0';
        break;
      }
    }

    return stepColor;
  }
}
